/**
 * Free Shipping Progress Bar
 * 
 * Updates the free shipping progress bar when cart changes occur
 */
class FreeShippingProgress extends HTMLElement {
  constructor() {
    super();
    // console.log('FreeShippingProgress: Initializing...');
    // Bind methods to preserve 'this' context
    this.updateProgressFromCart = this.updateProgressFromCart.bind(this);
    this.updateProgress = this.updateProgress.bind(this);
    // Debounce the update function to prevent rapid firing on multiple cart events
    this.debouncedUpdate = this.debounce(this.updateProgressFromCart, 300);
    this.setupProgressBar();
  }

  // Add debounce helper function
  debounce(func, wait) {
    let timeout;
    return function() {
      const context = this;
      const args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        func.apply(context, args);
      }, wait);
    };
  }

  // Format money helper function 
  formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    
    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      
      if (isNaN(number) || number == null) {
        return 0;
      }
      
      number = (number / 100.0).toFixed(precision);
      
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsAmount = parts[1] ? (decimal + parts[1]) : '';
      
      return dollarsAmount + centsAmount;
    }
    
    switch (format.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, '\'');
        break;
    }
    
    return format.replace(placeholderRegex, value);
  }

  setupProgressBar() {
    // console.log('FreeShippingProgress: Setting up progress bar...');
    
    // Ensure theme settings and strings are available
    if (!window.theme) {
      console.error('FreeShippingProgress: window.theme is not available.');
      return;
    }
    
    if (!window.theme.settings) {
      console.error('FreeShippingProgress: window.theme.settings is not available.');
      return;
    }
    
    if (!window.theme.strings) {
      console.error('FreeShippingProgress: window.theme.strings is not available.');
      return;
    }
    
    // console.log('FreeShippingProgress: Theme settings available:', {
    //   freeShippingLimit: window.theme.settings.freeShippingLimit,
    //   freeShippingMessage: window.theme.strings.freeShippingMessage,
    //   freeShippingMessageHtml: window.theme.strings.freeShippingMessageHtml
    // });
    
    this.freeShippingThreshold = parseFloat(window.theme.settings.freeShippingLimit) * 100;
    this.progressBar = this.querySelector('[data-progress-bar]');
    this.messageElement = this.querySelector('.free-shipping-progress__message');
    
    // Store reference to parent container
    this.container = this.closest('.drawer__free-shipping-progress, .cart__free-shipping-progress');
    
    // console.log('FreeShippingProgress: Container found:', !!this.container);
    
    if (!this.progressBar) {
      console.error('FreeShippingProgress: Progress bar element not found.');
      return;
    }
    
    if (!this.messageElement) {
      console.error('FreeShippingProgress: Message element not found.');
      return;
    }
    
    if (isNaN(this.freeShippingThreshold) || this.freeShippingThreshold <= 0) {
      console.error('FreeShippingProgress: Invalid free shipping threshold:', this.freeShippingThreshold);
      return;
    }
    
    // console.log('FreeShippingProgress: Setup successful, adding event listeners...');

    // Set initial state - add a slight delay to ensure cart.js is fully loaded
    setTimeout(() => {
      this.updateProgressFromCart();
    }, 100);
    
    // Listen for all possible cart-related events
    const events = [
      'cart:updated',        // General event
      'cart:refresh',        // Used by some themes
      'cart:change',         // Used by some themes
      'cart:build',          // Used by Impulse theme
      'cart:quantity',       // QtySelector event
      'product:added',       // When a product is added to cart
      'ajax:success'         // General AJAX success event that might include cart updates
    ];
    
    events.forEach(eventName => {
      document.addEventListener(eventName, this.debouncedUpdate);
    });
    
    // AJAX navigation detection
    const intersectionObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        // console.log('FreeShippingProgress: Element is now visible, updating');
        this.debouncedUpdate();
      }
    });
    intersectionObserver.observe(this);
    
    // Direct ajax listener for cart updates
    document.addEventListener('click', (event) => {
      // Listen for any clicks that might change the cart
      const target = event.target;
      if (target && (
        target.classList.contains('js-qty__adjust') || 
        target.closest('.js-qty__adjust') ||
        target.classList.contains('add-to-cart') ||
        target.closest('.add-to-cart') ||
        target.classList.contains('cart__remove') ||
        target.closest('.cart__remove') ||
        target.closest('form[action="/cart/add"]') ||
        target.closest('form[action="/cart/change"]') ||
        target.closest('form[action="/cart/update"]')
      )) {
        // console.log('FreeShippingProgress: Cart-related element clicked');
        setTimeout(this.debouncedUpdate, 500);
      }
    });
    
    // Also monitor cart form submissions
    const cartForms = document.querySelectorAll('form[action="/cart/add"], form[action="/cart/change"], form[action="/cart/update"]');
    cartForms.forEach(form => {
      form.addEventListener('submit', () => {
        // console.log('FreeShippingProgress: Cart form submitted');
        setTimeout(this.debouncedUpdate, 500);
      });
    });

    // Set a frequent polling interval as a fallback for missed events
    setInterval(this.debouncedUpdate, 2000);
  }

  updateProgressFromCart() {
    // console.log('FreeShippingProgress: Fetching updated cart data...');
    
    // Ensure we have access to the container
    if (!this.container) {
      this.container = this.closest('.drawer__free-shipping-progress, .cart__free-shipping-progress');
      // console.log('FreeShippingProgress: Container reference updated:', !!this.container);
    }
    
    fetch('/cart.js')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
       })
      .then(cart => {
        // console.log('FreeShippingProgress: Cart data received:', cart);
        this.updateProgress(cart);
      })
      .catch(error => console.error('FreeShippingProgress: Error fetching cart:', error));
  }

  updateProgress(cart) {
    if (!cart) {
      console.error('FreeShippingProgress: No cart data provided.');
      return;
    }
    
    if (!this.progressBar) {
      console.error('FreeShippingProgress: Progress bar element not available for update.');
      return;
    }
    
    if (!this.messageElement) {
      console.error('FreeShippingProgress: Message element not available for update.');
      return;
    }
    
    if (!window.theme || !window.theme.strings) {
      console.error('FreeShippingProgress: Theme settings not available for update.');
      return;
    }
    
    // console.log('FreeShippingProgress: Updating progress with cart total:', cart.total_price);
    
    const cartTotal = cart.total_price;
    
    // Check visibility range (55.00-75.00 CHF)
    const minShowAmount = 5500;  // 55.00 CHF in cents
    const maxShowAmount = 7500;  // 75.00 CHF in cents
    const shouldShow = cartTotal >= minShowAmount && cartTotal <= maxShowAmount;
    
    // console.log('FreeShippingProgress: Cart total in show range?', shouldShow, {
    //   cartTotal,
    //   minShowAmount,
    //   maxShowAmount
    // });
    
    // Toggle visibility based on new requirements
    if (shouldShow) {
      // Show when cart is between 55-75 CHF
      if (this.classList.contains('hide-element')) {
        // console.log('FreeShippingProgress: Removing hide-element class - cart total in visible range');
        this.classList.remove('hide-element');
        
        // Show the parent container
        if (this.container) {
          this.container.style.display = '';
        }
      }
    } else {
      // Hide when cart is less than 55 or more than 75 CHF
      if (!this.classList.contains('hide-element')) {
        // console.log('FreeShippingProgress: Adding hide-element class - cart total outside visible range');
        this.classList.add('hide-element');
        
        // Also hide the parent container for better compatibility
        if (this.container) {
          this.container.style.display = 'none';
        }
      }
    }
    
    // Only continue updating if visible
    if (shouldShow) {
      const remainingAmount = Math.max(0, this.freeShippingThreshold - cartTotal);
      const progressPercentage = Math.min((cartTotal / this.freeShippingThreshold) * 100, 100);
      
      // console.log('FreeShippingProgress: Calculated values:', {
      //   cartTotal,
      //   remainingAmount,
      //   progressPercentage,
      //   freeShippingThreshold: this.freeShippingThreshold
      // });
      
      this.progressBar.style.width = `${progressPercentage}%`;
      
      // ONLY update the message if we're eligible for free shipping
      // Otherwise let the server-rendered message handle it to maintain correct placement
      if (cartTotal >= this.freeShippingThreshold) {
        const newMessage = window.theme.strings.freeShippingEligible || 'You\'re eligible for free shipping!';
        // console.log('FreeShippingProgress: Using eligible message');
        
        if (this.messageElement.innerHTML !== newMessage) {
          // console.log('FreeShippingProgress: Updating eligible message in DOM');
          this.messageElement.innerHTML = newMessage;
        }
        return;
      }
      
      // *** For non-eligible status, we'll re-render the entire element from scratch ***
      // This ensures the message format and placeholders are exactly the same as the server-rendered version
      
      const formattedAmount = this.formatMoney(remainingAmount, window.theme.settings.moneyFormat || '${{amount}}');
      // console.log('FreeShippingProgress: Formatted amount:', formattedAmount);
      
      // Use the original message pattern but replace just the amount
      const originalMessageHTML = this.messageElement.innerHTML;
      
      // First try to detect if we can find the amount in the message
      const amountPattern = /(<[^>]+>)?([^<]+)(<\/[^>]+>)?/;
      const amountMatches = originalMessageHTML.match(amountPattern);
      
      if (amountMatches) {
        // Try to locate just the numerical part in the message
        const pricePattern = /\d+[\.,]\d+|\d+/; 
        const oldPrice = originalMessageHTML.match(pricePattern);
        
        if (oldPrice) {
          // console.log('FreeShippingProgress: Found price in message:', oldPrice[0]);
          
          // Use exact same format as original, just swap the amount
          const newMessageHTML = originalMessageHTML.replace(oldPrice[0], formattedAmount.match(/\d+[\.,]\d+|\d+/)[0]);
          
          if (this.messageElement.innerHTML !== newMessageHTML) {
            // console.log('FreeShippingProgress: Updating price in existing message:', newMessageHTML);
            this.messageElement.innerHTML = newMessageHTML;
          }
          return;
        }
      }
      
      // If we couldn't detect the price pattern, use the Liquid-rendered approach
      // This is a safer fallback that follows the template's original structure
      this.renderNewProgressBar(cart);
    }
  }
  
  // New method to completely re-render the progress bar using the same template structure
  renderNewProgressBar(cart) {
    // console.log('FreeShippingProgress: Re-rendering progress bar from template structure');
    
    const cartTotal = cart.total_price;
    const remainingAmount = Math.max(0, this.freeShippingThreshold - cartTotal);
    const formattedAmount = this.formatMoney(remainingAmount, window.theme.settings.moneyFormat || '${{amount}}');
    
    // console.log('FreeShippingProgress: Formatted remaining amount:', formattedAmount);
    
    // Instead of hardcoded message, try to get it from window.theme.strings first
    let newMessage = '';
    
    if (window.theme.strings.freeShippingMessageHtml) {
      // Use the HTML version from the translations if available
      newMessage = window.theme.strings.freeShippingMessageHtml.replace('{{ amount }}', formattedAmount);
      // console.log('FreeShippingProgress: Using freeShippingMessageHtml');
    } else if (window.theme.strings.freeShippingMessage) {
      // Fallback to regular message with highlight added
      newMessage = window.theme.strings.freeShippingMessage.replace('{{ amount }}', `<span class="cs-highlight">${formattedAmount}</span>`);
      // console.log('FreeShippingProgress: Using freeShippingMessage with highlight span');
    } else {
      // Ultimate fallback - use German text template as last resort
      const template = "Fügen Sie CHF_AMOUNT zu Ihrem Warenkorb hinzu, um kostenlosen Versand zu erhalten";
      newMessage = template.replace('CHF_AMOUNT', `<span class="cs-highlight">${formattedAmount}</span>`);
      // console.log('FreeShippingProgress: Using hardcoded template as last resort');
    }
    
    if (this.messageElement.innerHTML !== newMessage) {
      // console.log('FreeShippingProgress: Setting new message:', newMessage); 
      this.messageElement.innerHTML = newMessage;
    }
  }
}
 
// Add an event listener for DOMContentLoaded to ensure we can define the custom element
document.addEventListener('DOMContentLoaded', () => {
  // console.log('FreeShippingProgress: DOMContentLoaded event fired');
  defineCustomElement();
});

// Define it immediately for faster loading
defineCustomElement();

// Helper function to define the custom element
function defineCustomElement() {
  if (!customElements.get('free-shipping-progress')) {
    // console.log('FreeShippingProgress: Defining custom element');
    customElements.define('free-shipping-progress', FreeShippingProgress);
  } else {
    // console.log('FreeShippingProgress: Custom element already defined');
  }
} 